// ╔══════════════════════════════════════════════════════════════════╗
// ║                    ADMIN USERS PAGE                              ║
// ╚══════════════════════════════════════════════════════════════════╝

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  UserCog,
  Search,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  X,
  Save,
  RefreshCw,
  Shield,
  Mail,
  Phone,
  AlertCircle,
  CheckCircle,
  Lock,
  Unlock,
  Key,
  Calendar,
  Eye,
  EyeOff
} from 'lucide-react'
import { AdminUsersAPI } from '../../services/adminApi'
import { useAdmin } from '../AdminApp'

export default function AdminUsersPage() {
  const { hasPermission, admin: currentAdmin } = useAdmin()
  const [activeTab, setActiveTab] = useState('users')

  // Users state
  const [adminUsers, setAdminUsers] = useState([])
  const [loadingUsers, setLoadingUsers] = useState(true)

  // Roles state
  const [roles, setRoles] = useState([])
  const [loadingRoles, setLoadingRoles] = useState(false)

  // Common state
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('')

  // Modal
  const [showModal, setShowModal] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers()
    } else {
      fetchRoles()
    }
  }, [activeTab])

  useEffect(() => {
    // Fetch roles for the dropdown
    fetchRolesForDropdown()
  }, [])

  const fetchUsers = async () => {
    setLoadingUsers(true)
    setError(null)

    try {
      const res = await AdminUsersAPI.getAdminUsers()
      if (res.success) {
        setAdminUsers(res.data.admins)
      } else {
        setError(res.message)
      }
    } catch (err) {
      setError('Failed to load admin users')
    } finally {
      setLoadingUsers(false)
    }
  }

  const fetchRoles = async () => {
    setLoadingRoles(true)
    setError(null)

    try {
      const res = await AdminUsersAPI.getRoles()
      if (res.success) {
        setRoles(res.data.roles)
      } else {
        setError(res.message)
      }
    } catch (err) {
      setError('Failed to load roles')
    } finally {
      setLoadingRoles(false)
    }
  }

  const fetchRolesForDropdown = async () => {
    try {
      const res = await AdminUsersAPI.getRoles()
      if (res.success) {
        setRoles(res.data.roles)
      }
    } catch (err) {
      console.error('Failed to load roles')
    }
  }

  const handleSaveUser = async (userData) => {
    setProcessing(true)
    try {
      let res
      if (userData.id) {
        res = await AdminUsersAPI.updateAdminUser(userData.id, userData)
      } else {
        res = await AdminUsersAPI.createAdminUser(userData)
      }

      if (res.success) {
        fetchUsers()
        setShowModal(false)
        setEditingUser(null)
      } else {
        alert(res.message || 'Failed to save user')
      }
    } catch (err) {
      console.error('Failed to save user')
    } finally {
      setProcessing(false)
    }
  }

  const handleDeleteUser = async (userId) => {
    if (userId === currentAdmin?.id) {
      alert('You cannot delete your own account')
      return
    }

    if (!confirm('Are you sure you want to delete this admin user?')) return

    try {
      const res = await AdminUsersAPI.deleteAdminUser(userId)
      if (res.success) {
        fetchUsers()
      }
    } catch (err) {
      console.error('Failed to delete user')
    }
  }

  const handleToggleStatus = async (userId, currentStatus) => {
    if (userId === currentAdmin?.id) {
      alert('You cannot deactivate your own account')
      return
    }

    try {
      const res = await AdminUsersAPI.updateAdminUser(userId, {
        is_active: !currentStatus
      })
      if (res.success) {
        fetchUsers()
      }
    } catch (err) {
      console.error('Failed to toggle status')
    }
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const filteredUsers = adminUsers.filter(user => {
    const matchesSearch = !search ||
      user.first_name?.toLowerCase().includes(search.toLowerCase()) ||
      user.last_name?.toLowerCase().includes(search.toLowerCase()) ||
      user.email?.toLowerCase().includes(search.toLowerCase())
    const matchesRole = !roleFilter || user.role_id === roleFilter
    return matchesSearch && matchesRole
  })

  const getRoleBadgeColor = (roleName) => {
    const colors = {
      super_admin: 'bg-red-500/20 text-red-400',
      admin: 'bg-orange-500/20 text-orange-400',
      manager: 'bg-blue-500/20 text-blue-400',
      support: 'bg-emerald-500/20 text-emerald-400',
      analyst: 'bg-violet-500/20 text-violet-400',
      compliance: 'bg-yellow-500/20 text-yellow-400'
    }
    return colors[roleName] || 'bg-slate-500/20 text-slate-400'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Admin Users</h1>
          <p className="text-slate-400 mt-1">Manage administrator accounts and roles</p>
        </div>
        {hasPermission('admin_users.create') && activeTab === 'users' && (
          <button
            onClick={() => {
              setEditingUser(null)
              setShowModal(true)
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Admin
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700 pb-2">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'users'
              ? 'bg-blue-500 text-white'
              : 'text-slate-400 hover:bg-slate-700'
          }`}
        >
          Admin Users
        </button>
        <button
          onClick={() => setActiveTab('roles')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'roles'
              ? 'bg-blue-500 text-white'
              : 'text-slate-400 hover:bg-slate-700'
          }`}
        >
          Roles & Permissions
        </button>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <>
          {/* Filters */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
              </div>

              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
              >
                <option value="">All Roles</option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>{role.display_name}</option>
                ))}
              </select>

              <button
                onClick={fetchUsers}
                className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
            {loadingUsers ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
                  <p className="text-slate-400">{error}</p>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700 bg-slate-900/50">
                      <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Admin</th>
                      <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Email</th>
                      <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Role</th>
                      <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Status</th>
                      <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Last Login</th>
                      <th className="text-center py-4 px-4 text-sm font-medium text-slate-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                              <span className="text-white font-semibold">
                                {user.first_name?.[0]}{user.last_name?.[0]}
                              </span>
                            </div>
                            <div>
                              <p className="text-white font-medium">
                                {user.first_name} {user.last_name}
                                {user.id === currentAdmin?.id && (
                                  <span className="ml-2 text-xs text-blue-400">(You)</span>
                                )}
                              </p>
                              <p className="text-slate-500 text-sm">Created {formatDate(user.created_at)}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-slate-300">{user.email}</span>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getRoleBadgeColor(user.role_name)}`}>
                            {user.role_display_name || user.role_name}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            user.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                          }`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-slate-400 text-sm">
                          {user.last_login_at ? formatDate(user.last_login_at) : 'Never'}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center justify-center gap-2">
                            {hasPermission('admin_users.edit') && (
                              <>
                                <button
                                  onClick={() => {
                                    setEditingUser(user)
                                    setShowModal(true)
                                  }}
                                  className="p-2 hover:bg-slate-600 rounded-lg transition-colors"
                                  title="Edit"
                                >
                                  <Edit2 className="w-4 h-4 text-slate-400" />
                                </button>
                                <button
                                  onClick={() => handleToggleStatus(user.id, user.is_active)}
                                  className="p-2 hover:bg-slate-600 rounded-lg transition-colors"
                                  title={user.is_active ? 'Deactivate' : 'Activate'}
                                  disabled={user.id === currentAdmin?.id}
                                >
                                  {user.is_active ? (
                                    <Lock className="w-4 h-4 text-yellow-400" />
                                  ) : (
                                    <Unlock className="w-4 h-4 text-emerald-400" />
                                  )}
                                </button>
                              </>
                            )}
                            {hasPermission('admin_users.delete') && user.id !== currentAdmin?.id && (
                              <button
                                onClick={() => handleDeleteUser(user.id)}
                                className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4 text-red-400" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Roles Tab */}
      {activeTab === 'roles' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {loadingRoles ? (
            <div className="col-span-full flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
          ) : error ? (
            <div className="col-span-full flex items-center justify-center py-12">
              <div className="text-center">
                <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
                <p className="text-slate-400">{error}</p>
              </div>
            </div>
          ) : (
            roles.map((role) => (
              <motion.div
                key={role.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-slate-800 border border-slate-700 rounded-xl p-5"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      role.name === 'super_admin' ? 'bg-red-500/20' :
                      role.name === 'admin' ? 'bg-orange-500/20' :
                      'bg-blue-500/20'
                    }`}>
                      <Shield className={`w-6 h-6 ${
                        role.name === 'super_admin' ? 'text-red-400' :
                        role.name === 'admin' ? 'text-orange-400' :
                        'text-blue-400'
                      }`} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">{role.display_name}</h3>
                      <p className="text-slate-400 text-sm font-mono">{role.name}</p>
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-xs">
                    {adminUsers.filter(u => u.role_id === role.id).length} users
                  </span>
                </div>

                <p className="text-slate-400 text-sm mb-4">{role.description}</p>

                {role.name === 'super_admin' ? (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-red-400 text-sm">
                      <Shield className="w-4 h-4 inline-block mr-2" />
                      Full system access - all permissions granted
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-slate-400 text-sm font-medium">Permissions:</p>
                    <div className="flex flex-wrap gap-1">
                      {role.permissions?.slice(0, 6).map((perm, i) => (
                        <span key={i} className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-xs">
                          {perm}
                        </span>
                      ))}
                      {role.permissions?.length > 6 && (
                        <span className="px-2 py-1 bg-slate-700 text-slate-400 rounded text-xs">
                          +{role.permissions.length - 6} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* User Modal */}
      <AnimatePresence>
        {showModal && (
          <AdminUserFormModal
            user={editingUser}
            roles={roles}
            onClose={() => {
              setShowModal(false)
              setEditingUser(null)
            }}
            onSave={handleSaveUser}
            processing={processing}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

// Admin User Form Modal Component
function AdminUserFormModal({ user, roles, onClose, onSave, processing }) {
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    password: '',
    role_id: user?.role_id || '',
    is_active: user?.is_active ?? true
  })
  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = (e) => {
    e.preventDefault()

    // Validate
    if (!user && !formData.password) {
      alert('Password is required for new users')
      return
    }

    const data = { ...formData }
    if (user) {
      data.id = user.id
      // Don't send empty password for updates
      if (!data.password) delete data.password
    }

    onSave(data)
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="p-6 border-b border-slate-700 flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">
              {user ? 'Edit Admin User' : 'Add Admin User'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">First Name</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  placeholder="John"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Last Name</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  placeholder="Doe"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  placeholder="admin@securebank.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Password {user && <span className="text-slate-500">(leave empty to keep current)</span>}
              </label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full pl-10 pr-12 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  placeholder={user ? '••••••••' : 'Enter password'}
                  required={!user}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Role</label>
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <select
                  value={formData.role_id}
                  onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500 appearance-none"
                  required
                >
                  <option value="">Select a role</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>{role.display_name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-5 h-5 bg-slate-900/50 border border-slate-600 rounded"
              />
              <label htmlFor="is_active" className="text-slate-300">Active (can login)</label>
            </div>
          </div>

          <div className="p-6 border-t border-slate-700 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={processing}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {processing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {user ? 'Update Admin' : 'Create Admin'}
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  )
}
